import { useState, useMemo } from "react";
import type { ComboboxOption } from "ui";

interface ErrorCodeData {
  label: string;
  value: number;
}

export function useErrorCodeFilter(allDeclineReasons: ErrorCodeData[]) {
  const MAX_SELECTION = 4;

  const [selectedErrorCodes, setSelectedErrorCodes] = useState<
    ComboboxOption<string>[]
  >([]);

  // Convert error codes to dropdown options
  const errorCodeOptions = useMemo(
    () =>
      allDeclineReasons.map(
        (item) => [item.label, item.label] as ComboboxOption<string>
      ),
    [allDeclineReasons]
  );

  // Disable unselected items when max selection is reached
  const isItemDisabled = (
    item: ComboboxOption<string>,
    _index: number,
    currentSelection?: ComboboxOption<string>[] | ComboboxOption<string> | null
  ) => {
    // Use currentSelection (internal buffer) if provided, otherwise fall back to selectedErrorCodes
    const selection = (currentSelection ||
      selectedErrorCodes) as ComboboxOption<string>[];

    const isSelected = selection.some(([value]) => value === item[0]);
    // Allow deselecting, but prevent selecting new items when at max
    return !isSelected && selection.length >= MAX_SELECTION;
  };

  // Compute filtered data based on selection
  const { filteredData, hasOthers, othersCount } = useMemo(() => {
    // Case 1: No selection - show top 4 + OTHERS (if more than 4 total)
    if (selectedErrorCodes.length === 0) {
      if (allDeclineReasons.length <= MAX_SELECTION) {
        // 4 or fewer total - show all, no OTHERS
        return {
          filteredData: [...allDeclineReasons],
          hasOthers: false,
          othersCount: 0,
        };
      }

      // More than 4 - show top 4 + OTHERS
      const top4 = allDeclineReasons.slice(0, MAX_SELECTION);
      const others = allDeclineReasons.slice(MAX_SELECTION);
      const othersSum = others.reduce((sum, item) => sum + item.value, 0);

      return {
        filteredData: [...top4, { label: "OTHERS", value: othersSum }],
        hasOthers: true,
        othersCount: othersSum,
      };
    }

    // Case 2: Subset selected (1-4 items) - show selected + OTHERS (if any unselected)
    const selectedLabels = selectedErrorCodes.map(([value]) => value);
    const selectedItems = allDeclineReasons.filter((item) =>
      selectedLabels.includes(item.label)
    );
    const unselectedItems = allDeclineReasons.filter(
      (item) => !selectedLabels.includes(item.label)
    );
    const othersSum = unselectedItems.reduce(
      (sum, item) => sum + item.value,
      0
    );

    return {
      filteredData:
        othersSum > 0
          ? [...selectedItems, { label: "OTHERS", value: othersSum }]
          : selectedItems,
      hasOthers: othersSum > 0,
      othersCount: othersSum,
    };
  }, [selectedErrorCodes, allDeclineReasons]);

  return {
    selectedErrorCodes,
    setSelectedErrorCodes,
    errorCodeOptions,
    filteredData,
    hasOthers,
    othersCount,
    isItemDisabled,
  };
}
